import { Link } from "react-router-dom";
import SEOHead from "@/components/SEOHead";
import SEOPageLayout from "@/components/SEOPageLayout";
import { REUNION_KEYWORDS, joinKw } from "@/config/keywords";

const HERO_IMG =
  "https://customer-assets.emergentagent.com/job_custom-team-jerseys-1/artifacts/yzzktg66_Family%20reunion%20uniform%20polo%20shirt.png";

export default function ReunionPoloShirts() {
  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "Service",
    name: "Reunion Polo Shirts",
    serviceType: "Custom family and village reunion polo shirts",
    provider: {
      "@type": "LocalBusiness",
      name: "Tribal Designz",
      address: { "@type": "PostalAddress", addressLocality: "Auckland", addressCountry: "NZ" },
      telephone: "+64 22 101 2443",
    },
  };

  const imageObjects = [{
    url: HERO_IMG,
    alt: "Samoan Family Reunion Polo Shirts — Manu'a family in matching custom Pacific Heritage Uniforms NZ",
    name: "Reunion Legacy Collection — Manu'a Family Reunion",
    representative: true,
  }];

  return (
    <>
      <SEOHead
        title="Reunion Polo Shirts NZ | Family & Village Reunions"
        description="Reunion Polo Shirts NZ — custom Samoan, Tongan & Cook Islands family and village reunion uniforms. Auckland-based studio. Free design mockup in 24h, group pricing from $49."
        path="/reunion-polo-shirts"
        image={HERO_IMG}
        imageAlt="Samoan Family Reunion Polo Shirts — Pacific Heritage Uniforms NZ"
        keywords={joinKw(REUNION_KEYWORDS)}
        jsonLd={jsonLd}
        imageObjects={imageObjects}
      />
      <SEOPageLayout
        heroEyebrow="Reunion Legacy Collection™"
        heroH1="Reunion Polo Shirts — Custom Designs for Your Family or Village"
        heroSub="Custom reunion polo shirts that carry your family name, village colours and shared history. Designed in Auckland and delivered NZ-wide."
        heroImage={HERO_IMG}
        heroImageAlt="Samoan family reunion custom polo shirts NZ"
        breadcrumbs={[{ label: "Home", href: "/" }, { label: "Reunion Polo Shirts" }]}
      >
        <h2>The keepsake that makes your reunion unforgettable</h2>
        <p>
          A Pacific family reunion is more than a get-together. It's a
          gathering of generations — uncles flying in from Australia,
          cousins arriving from Samoa, Tonga or the Cook Islands, kids
          meeting second cousins for the first time. Years of planning,
          fundraising, and emotion all converge on one weekend. The
          <strong> reunion polo</strong> is what your aiga or kainga keeps
          long after the food's been eaten and the singing's done.
        </p>
        <p>
          Tribal Designz designs <strong>custom reunion polo shirts</strong>{" "}
          for Samoan, Tongan, Cook Islands, Fijian, Niuean and pan-Pacific
          family and village reunions across New Zealand. Each design is
          built to carry your specific story — your family name, your
          village, your year, your matriarch and patriarch, your colours.
        </p>

        <h2>Common reunion polo design elements</h2>
        <ul>
          <li><strong>Family name (aiga / kainga / kopu tangata)</strong> on chest or back</li>
          <li><strong>Year</strong> of the reunion, often as large statement print on the back</li>
          <li><strong>Village / island of origin</strong></li>
          <li><strong>Matai title or matriarch / patriarch name</strong></li>
          <li><strong>Cultural pattern overlay</strong> — siapo, ngatu, tivaevae, tatau line work</li>
          <li><strong>Generation / branch identifier</strong> (some families use colour bands by branch)</li>
          <li><strong>Family tree shorthand</strong> (e.g. founders' names listed)</li>
          <li><strong>Scripture verse</strong> — common for Christian Pacific reunions</li>
        </ul>

        <h2>Tiered designs by generation or branch</h2>
        <p>
          Many reunions choose to tier their polos:
        </p>
        <ul>
          <li>One colour for the elders (matua sā / kupuna)</li>
          <li>One for the founding-couple's adult children</li>
          <li>One for grandchildren</li>
          <li>One for great-grandchildren</li>
        </ul>
        <p>
          This lets attendees see the generational structure at a glance,
          and makes for unforgettable group photos. We design these tiered
          variations so they're clearly distinct but visually unified — same
          base design, different colour or accent.
        </p>

        <h2>Village reunions</h2>
        <p>
          Village reunions (especially for Samoan and Cook Islands diaspora
          villages) bring together everyone with ancestral ties to a
          specific village — often hundreds of attendees. We've designed
          uniforms for village reunions including the village name, village
          crest, district, founding family lines, and event year. For these
          larger projects we issue custom quotes — see our <a href="/#pricing">
          pricing</a> for the standard tiers.
        </p>

        <h2>Sizing for the whole family</h2>
        <p>
          We carry polo blanks from XS to 7XL — important when you're
          fitting kids, aunties, uncles and matua all in one order. Sizing
          forms can be circulated digitally to family members ahead of
          production.
        </p>

        <h2>Order well in advance</h2>
        <p>
          Reunions are usually planned a year out, so lock in your polo
          order at least 8 weeks before the event. This leaves room for
          design rounds, sizing chase-ups with the whole family, and
          production. Last-minute reunion orders are doable but stressful —
          start the conversation early.
        </p>

        <h2>Related</h2>
        <p>
          New to planning a reunion?{" "}
          <Link to="/blog/pacific-family-reunion-polo-shirt-checklist">
            Read our Pacific Family Reunion Polo Shirt Checklist
          </Link>{" "}
          — a step-by-step guide covering numbers, themes, colours, sizing,
          group photos and lead times.
        </p>
        <p>
          <Link to="/samoan-church-uniforms">Samoan church uniforms</Link> ·{" "}
          <Link to="/tongan-church-uniforms">Tongan church uniforms</Link> ·{" "}
          <Link to="/cook-islands-uniforms">Cook Islands uniforms</Link> ·{" "}
          <Link to="/church-conference-uniforms">Conference uniforms</Link>
        </p>
      </SEOPageLayout>
    </>
  );
}
